
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../../api";
import { BASE_URL } from "../../config";
import DateInput from "../../components/DateInput";
import "../../Styles/Documents.css";
import { showSuccess, showError, showConfirm } from "../../utils/dialog";
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from "../../utils/staffDocumentValidation";
import talentLogo from "../../assets/talentlogo2.png";

const requiredDocs = ["SIN Document", "Identity Document", "Resume and Other"];
// Documents uploaded through the File + Expiry date modal (others use a plain file picker)
const MODAL_DOC_TYPES = ["SIN Document", "Identity Document"];

// SIN Document expiry is optional only for Canadian Citizens / PR (Profile residency "citizen");
// every other residency status (Work Permit, Study Permit, Refugee) must provide one.
const SIN_EXPIRY_OPTIONAL_RESIDENCY = "citizen";
const SIN_EXPIRY_REQUIRED_MSG = "Expiry date is required for your Residency Status.";

function defaultDocEntry(type) {
  return { type, url: "", file_id: null, expiry_date: "" };
}

function splitRegistrationFilesPayload(data) {
  const raw = data && typeof data === "object" ? data : {};
  const meta = raw.__meta && typeof raw.__meta === "object" ? raw.__meta : {};
  const urls = { ...raw };
  delete urls.__meta;
  return { urls, meta };
}

const Documents = () => {
  const navigate = useNavigate();
  const accountId = localStorage.getItem("accountId");
  const itemId = "documents";
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [status, setStatus] = useState("incomplete"); // tracks overall doc completion
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogDocType, setDialogDocType] = useState("");
  const [dialogSinExpiry, setDialogSinExpiry] = useState("");
  const [savingSinExpiry, setSavingSinExpiry] = useState(false);
  // Per-doc loading state: { "SIN Document": true/false, ... }
  const [uploadingDoc, setUploadingDoc] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docModalFile, setDocModalFile] = useState(null);
  const [docModalExpiry, setDocModalExpiry] = useState("");
  const [sinExpiryError, setSinExpiryError] = useState("");
  const [docModalType, setDocModalType] = useState("SIN Document");
  const [docModalFileError, setDocModalFileError] = useState("");

  // Residency Status chosen on the Profile step: local draft first, then the saved profile
  const [residency, setResidency] = useState(() => {
    try {
      const draft = JSON.parse(localStorage.getItem(`form_${accountId}_profile`) || "{}");
      return draft.residency || "";
    } catch {
      return "";
    }
  });
  useEffect(() => {
    if (residency) return;
    API.get("/api/registration/me/profile")
      .then((res) => setResidency(res.data?.residency || res.data?.residency_status || ""))
      .catch((err) => console.warn("[Documents] Could not load residency status:", err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Unknown residency is treated as required (the stricter rule)
  const isSinExpiryRequired = residency !== SIN_EXPIRY_OPTIONAL_RESIDENCY;
  const sinExpiryLabel = isSinExpiryRequired ? (
    <span>Expiry date <span style={{ color: "#b00020" }}>*</span></span>
  ) : (
    <span>Expiry date (optional)</span>
  );

  // Helpers: canonical storage as list to avoid accidental overwrites
  const toListFromMap = (map) => {
    return requiredDocs.map((type) => ({
      type,
      url: map[type] || "",
      file_id: null,
      expiry_date: "",
    }));
  };
  const toMapFromList = (list) => {
    const map = {};
    (Array.isArray(list) ? list : []).forEach((d) => {
      if (d && d.type && d.url) map[d.type] = d.url;
    });
    return map;
  };
  const loadDocsList = () => {
    try {
      const raw = localStorage.getItem(`docsList_${accountId}`);
      const list = JSON.parse(raw || "null");
      if (Array.isArray(list)) {
        return requiredDocs.map((type) => {
          const found = list.find((d) => d?.type === type);
          if (found && typeof found === "object") {
            return {
              type,
              url: found.url || "",
              file_id: found.file_id != null ? found.file_id : null,
              expiry_date: found.expiry_date || "",
            };
          }
          return defaultDocEntry(type);
        });
      }
    } catch {}
    // Fallback: build from legacy uploadedDocs object
    try {
      const legacyRaw = localStorage.getItem(`uploadedDocs_${accountId}`);
      const legacyMap = JSON.parse(legacyRaw || "{}") || {};
      return toListFromMap(legacyMap);
    } catch {}
    // Default empty list with required types
    return requiredDocs.map((type) => defaultDocEntry(type));
  };
  const saveDocsList = (list) => {
    const normalized = requiredDocs.map((type) => {
      const found = (Array.isArray(list) ? list : []).find((d) => d?.type === type);
      return {
        type,
        url: found?.url || "",
        file_id: found?.file_id != null ? found.file_id : null,
        expiry_date: found?.expiry_date || "",
      };
    });
    localStorage.setItem(`docsList_${accountId}`, JSON.stringify(normalized));
    const map = toMapFromList(normalized);
    localStorage.setItem(`uploadedDocs_${accountId}`, JSON.stringify(map)); // backward compat
    return normalized;
  };

  const mergeServerFiles = (serverData) => {
    const { urls, meta } = splitRegistrationFilesPayload(serverData);
    const currentList = loadDocsList();
    return currentList.map((entry) => {
      const m = meta[entry.type] || {};
      const url = urls[entry.type] || entry.url || "";
      return {
        type: entry.type,
        url,
        file_id: m.file_id != null ? m.file_id : entry.file_id,
        expiry_date:
          m.expiry_date != null && m.expiry_date !== ""
            ? String(m.expiry_date).slice(0, 10)
            : entry.expiry_date || "",
      };
    });
  };

  // Load saved documents from backend (or localStorage fallback)
  useEffect(() => {
    // 1) Hydrate from localStorage list immediately to avoid UI flicker/erase on relogin
    const deletedDocsStr = localStorage.getItem(`deletedDocs_${accountId}`);
    const deleted = (() => { try { const d = JSON.parse(deletedDocsStr || '[]'); return Array.isArray(d) ? d : []; } catch { return []; } })();
    const list = loadDocsList();
    const filteredList = list.map((d) => deleted.includes(d.type) ? { ...d, url: "", file_id: null, expiry_date: "" } : d);
    setUploadedDocs(toMapFromList(filteredList));

    // 2) Then fetch from backend and merge (backend wins when it has a value)
    const fetchDocs = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/registration/${accountId}/files`);
        const deletedDocsStr2 = localStorage.getItem(`deletedDocs_${accountId}`);
        const deleted2 = (() => { try { const d = JSON.parse(deletedDocsStr2 || '[]'); return Array.isArray(d) ? d : []; } catch { return []; } })();
        const mergedList = mergeServerFiles(res.data || {}).map((d) =>
          deleted2.includes(d.type) ? { ...d, url: "", file_id: null, expiry_date: "" } : d,
        );
        saveDocsList(mergedList);
        setUploadedDocs(toMapFromList(mergedList));
      } catch (err) {
        console.error("Error fetching docs:", err);
        // Keep whatever is in localStorage (already set above)
      }
    };
    if (accountId) {
      fetchDocs();
    }
  }, [accountId]);

  // Auto-save draft. Never set "complete" here (only on explicit Submit). If already complete, preserve it when user reopens and goes back.
  // When uploadedDocs is still empty on first load, do not overwrite "complete" with "incomplete" (data may not be hydrated yet).
  useEffect(() => {
    const allEmpty = requiredDocs.every((doc) => !uploadedDocs[doc]);
    const allFilled = requiredDocs.every((doc) => uploadedDocs[doc]);
    const newStatus = allEmpty ? "incomplete" : allFilled ? "complete" : "in-progress";
    setStatus(newStatus);
    const existing = accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null;
    const existingStatus = existing || localStorage.getItem(`status_${itemId}`);
    const statusToPersist = allEmpty
      ? (existingStatus === "complete" ? "complete" : "incomplete")
      : (existingStatus === "complete" ? "complete" : "in-progress");
    if (accountId) {
      localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    } else {
      localStorage.setItem(`status_${itemId}`, statusToPersist);
    }
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(uploadedDocs));
    if (accountId && statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [uploadedDocs]);

  // Spinner icon used inside buttons
  const Spinner = () => (
    <svg
      className="animate-spin"
      style={{ display: "inline", width: 14, height: 14, marginRight: 6, verticalAlign: "middle" }}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );

  const refreshAfterUpload = async (docType, resData) => {
    try {
      const filesRes = await axios.get(`${BASE_URL}/api/registration/${accountId}/files`);
      const list = loadDocsList();
      let next = mergeServerFiles(filesRes.data || {});
      next = next.map((d) => {
        if (d.type !== docType) return d;
        return {
          ...d,
          file_id: resData?.fileId != null ? resData.fileId : d.file_id,
          expiry_date:
            resData?.expiry_date != null
              ? String(resData.expiry_date).slice(0, 10)
              : d.expiry_date,
        };
      });
      saveDocsList(next);
      setUploadedDocs(toMapFromList(next));
      try {
        const raw = localStorage.getItem(`deletedDocs_${accountId}`);
        const dels = (() => { try { const d = JSON.parse(raw || '[]'); return Array.isArray(d) ? d : []; } catch { return []; } })();
        const idx = dels.indexOf(docType);
        if (idx >= 0) {
          dels.splice(idx, 1);
          localStorage.setItem(`deletedDocs_${accountId}`, JSON.stringify(dels));
        }
      } catch {}
    } catch (fetchErr) {
      console.error("Failed to refresh files after upload:", fetchErr);
      const list = loadDocsList();
      const next = list.map((d) =>
        d.type === docType
          ? {
              ...d,
              url: resData?.s3Key || d.url,
              file_id: resData?.fileId != null ? resData.fileId : d.file_id,
              expiry_date:
                resData?.expiry_date != null
                  ? String(resData.expiry_date).slice(0, 10)
                  : d.expiry_date,
            }
          : d,
      );
      saveDocsList(next);
      setUploadedDocs(toMapFromList(next));
      try {
        const raw = localStorage.getItem(`deletedDocs_${accountId}`);
        const dels = (() => { try { const d = JSON.parse(raw || '[]'); return Array.isArray(d) ? d : []; } catch { return []; } })();
        const idx = dels.indexOf(docType);
        if (idx >= 0) {
          dels.splice(idx, 1);
          localStorage.setItem(`deletedDocs_${accountId}`, JSON.stringify(dels));
        }
      } catch {}
    }
  };

  const uploadSingleFile = async (docType, file, expiryYmd) => {
    const validationMessage = validateStaffDocumentFile(file);
    if (validationMessage) {
      showError(validationMessage);
      return;
    }
    setUploadingDoc((prev) => ({ ...prev, [docType]: true }));
    const formData = new FormData();
    formData.append("file", file);
    formData.append("docType", docType);
    if (MODAL_DOC_TYPES.includes(docType) && expiryYmd) {
      formData.append("expiry_date", expiryYmd);
    }
    try {
      const res = await axios.post(`${BASE_URL}/api/registration/${accountId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshAfterUpload(docType, res.data);
      showSuccess(`${docType} uploaded successfully!`);
    } catch (err) {
      console.error(err);
      showError("Upload failed, please try again.");
    } finally {
      setUploadingDoc((prev) => ({ ...prev, [docType]: false }));
    }
  };

  // File upload handler (SIN / Identity: File + Expiry date modal; others: plain file picker)
  const handleAddOrUpdateDocument = async (docType) => {
    if (MODAL_DOC_TYPES.includes(docType)) {
      const row = loadDocsList().find((d) => d.type === docType);
      setDocModalType(docType);
      setDocModalExpiry(row?.expiry_date ? String(row.expiry_date).slice(0, 10) : "");
      setDocModalFile(null);
      setSinExpiryError("");
      setDocModalFileError("");
      setDocModalOpen(true);
      return;
    }

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = STAFF_DOCUMENT_ACCEPT;

    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      await uploadSingleFile(docType, file, "");
    };

    fileInput.click();
  };

  const submitDocModal = async () => {
    const docType = docModalType;
    const row = loadDocsList().find((d) => d.type === docType);
    // A file is mandatory unless one is already uploaded (then Save may just update the expiry)
    const fileMissing = !docModalFile && !row?.file_id;
    const expiryMissing = docType === "SIN Document" && isSinExpiryRequired && !docModalExpiry;
    setDocModalFileError(fileMissing ? "Please attach a file." : "");
    setSinExpiryError(expiryMissing ? SIN_EXPIRY_REQUIRED_MSG : "");
    if (fileMissing || expiryMissing) return;

    if (docModalFile) {
      await uploadSingleFile(docType, docModalFile, docModalExpiry);
      setDocModalOpen(false);
      setDocModalFile(null);
      return;
    }
    setSavingSinExpiry(true);
    try {
      await axios.patch(
        `${BASE_URL}/api/registration/${accountId}/files/${row.file_id}/expiry`,
        { expiry_date: docModalExpiry || null },
      );
      const filesRes = await axios.get(`${BASE_URL}/api/registration/${accountId}/files`);
      const merged = mergeServerFiles(filesRes.data || {});
      saveDocsList(merged);
      setUploadedDocs(toMapFromList(merged));
      setDocModalOpen(false);
      showSuccess(`${docType} expiry saved.`);
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.error || "Could not save expiry date.");
    } finally {
      setSavingSinExpiry(false);
    }
  };

  const renderButton = (docType) => {
    const isUploading = !!uploadingDoc[docType];
    return (
      <button
        className="add-button"
        onClick={() => handleAddOrUpdateDocument(docType)}
        disabled={isUploading}
        style={{ opacity: isUploading ? 0.7 : 1, cursor: isUploading ? "not-allowed" : "pointer", minWidth: 70 }}
      >
        {isUploading ? (
          <><Spinner />Uploading...</>
        ) : (
          uploadedDocs[docType] ? "Edit" : "Add"
        )}
      </button>
    );
  };

  const openDialogForDoc = async (docType) => {
    const val = uploadedDocs[docType];
    if (!val) {
      showError("No file uploaded for this document yet.");
      return;
    }
    const looksLikeKey = val && !/^https?:\/\//i.test(val);
    if (looksLikeKey && accountId) {
      try {
        const filesRes = await axios.get(`${BASE_URL}/api/registration/${accountId}/files`);
        const deletedDocsStr3 = localStorage.getItem(`deletedDocs_${accountId}`);
        const deleted3 = (() => { try { const d = JSON.parse(deletedDocsStr3 || '[]'); return Array.isArray(d) ? d : []; } catch { return []; } })();
        const merged = mergeServerFiles(filesRes.data || {}).map((d) =>
          deleted3.includes(d.type) ? { ...d, url: "", file_id: null, expiry_date: "" } : d,
        );
        saveDocsList(merged);
        setUploadedDocs(toMapFromList(merged));
      } catch (e) {
        console.error("Failed to refresh files:", e);
      }
    }
    const row = loadDocsList().find((d) => d.type === docType);
    setDialogSinExpiry(row?.expiry_date ? String(row.expiry_date).slice(0, 10) : "");
    setSinExpiryError("");
    setDialogDocType(docType);
    setIsDialogOpen(true);
  };

  const saveSinExpiryFromView = async () => {
    const row = loadDocsList().find((d) => d.type === "SIN Document");
    if (!row?.file_id) {
      showError("SIN file is not available to update yet. Upload again.");
      return;
    }
    if (isSinExpiryRequired && !dialogSinExpiry) {
      setSinExpiryError(SIN_EXPIRY_REQUIRED_MSG);
      return;
    }
    setSavingSinExpiry(true);
    try {
      await axios.patch(
        `${BASE_URL}/api/registration/${accountId}/files/${row.file_id}/expiry`,
        { expiry_date: dialogSinExpiry || null },
      );
      const filesRes = await axios.get(`${BASE_URL}/api/registration/${accountId}/files`);
      const merged = mergeServerFiles(filesRes.data || {});
      saveDocsList(merged);
      setUploadedDocs(toMapFromList(merged));
      showSuccess("Expiry date saved.");
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.error || "Could not save expiry date.");
    } finally {
      setSavingSinExpiry(false);
    }
  };

  const docsList = loadDocsList();
  const sinRow = docsList.find((d) => d.type === "SIN Document");
  const dialogUrl = dialogDocType ? uploadedDocs[dialogDocType] : "";

  return (
    <div className="documents-page">
      <div className="document-container">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
          <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 48, marginBottom: 8 }} />
        </div>
        <h1 className="documents-title" style={{ textAlign: "center" }}>Documents</h1>

        <div className="documents-layout">
          <div className="documents-list">
            {requiredDocs.map((docType) => (
              <div className="document-item" key={docType}>
                <div className="document-info">
                  <h3>{docType}</h3>
                  <p>Please upload your {docType}.</p>
                  {(() => {
                    const expiry = MODAL_DOC_TYPES.includes(docType) && docsList.find((d) => d.type === docType)?.expiry_date;
                    if (!expiry) return null;
                    const optional = !(docType === "SIN Document" && isSinExpiryRequired);
                    return (
                      <p className="documents-expiry-hint" style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
                        Expiry date{optional ? " (optional)" : ""}: {String(expiry).slice(0, 10)}
                      </p>
                    );
                  })()}
                  {uploadedDocs[docType] && (
                    <div className="document-actions">
                      <button
                        type="button"
                        className="view-dialog-button"
                        onClick={() => openDialogForDoc(docType)}
                      >
                        View
                      </button>
                    </div>
                  )}
                </div>
                {renderButton(docType)}
              </div>
            ))}

            <div className="form-actions">
              <button
                type="button"
                className="back-button"
                onClick={() => navigate(-1)}
              >
                BACK
              </button>
              <button
                type="submit"
                className="submit-doc-button"
                disabled={isSubmitting}
                style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? "not-allowed" : "pointer" }}
                onClick={async () => {
                  localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(uploadedDocs));
                  const allFilled = requiredDocs.every((doc) => uploadedDocs[doc]);

                  if (!allFilled) {
                    showError("Please upload all required documents before submitting.");
                    return;
                  }
                  // Catches a SIN Document uploaded without expiry before this rule applied
                  if (isSinExpiryRequired && !sinRow?.expiry_date) {
                    showError("Please add an expiry date to your SIN Document before submitting.");
                    return;
                  }

                  setIsSubmitting(true);
                  try {
                    const newStatus = 'complete';
                    if (accountId) {
                      localStorage.setItem(`status_${accountId}_${itemId}`, newStatus);
                    }
                    const payload = { status: newStatus, formData: uploadedDocs };
                    if (accountId) {
                      await axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
                        .catch(() => console.error('Failed to save documents'));
                      await axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} })
                        .catch(() => {});
                    }
                    showSuccess("Form completed successfully!");
                    navigate("/register");
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin"
                      style={{ display: "inline", width: 14, height: 14, marginRight: 6, verticalAlign: "middle" }}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  "SUBMIT"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {docModalOpen && (
        <div
          className="documents-modal-overlay"
          onClick={() => !savingSinExpiry && !uploadingDoc[docModalType] && setDocModalOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="documents-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 8,
              width: 440,
              maxWidth: "90%",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              overflow: "hidden",
            }}
          >
            <div className="documents-modal-header" style={{ padding: "12px 16px", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div className="documents-modal-title" style={{ fontWeight: 600 }}>{docModalType}</div>
              <button type="button" className="documents-modal-close" onClick={() => !savingSinExpiry && setDocModalOpen(false)} style={{ border: 0, background: "transparent", fontSize: 18, cursor: "pointer" }}>×</button>
            </div>
            <div className="documents-modal-body" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
                <span>File {!uploadedDocs[docModalType] ? <span style={{ color: "#b00020" }}>*</span> : <span style={{ color: "#666" }}>(optional if only updating expiry)</span>}</span>
                <input
                  type="file"
                  accept={STAFF_DOCUMENT_ACCEPT}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    const validationMessage = f && validateStaffDocumentFile(f);
                    if (validationMessage) {
                      showError(validationMessage);
                      e.target.value = "";
                      setDocModalFile(null);
                      return;
                    }
                    setDocModalFile(f);
                    if (f) setDocModalFileError("");
                  }}
                />
                {docModalFileError && <span style={{ color: "#b00020", fontSize: 12 }}>{docModalFileError}</span>}
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
                {docModalType === "SIN Document" ? sinExpiryLabel : <span>Expiry date (optional)</span>}
                <DateInput
                  className="documents-expiry-input"
                  value={docModalExpiry}
                  onChange={(v) => { setDocModalExpiry(v); if (v) setSinExpiryError(""); }}
                />
                {sinExpiryError && <span style={{ color: "#b00020", fontSize: 12 }}>{sinExpiryError}</span>}
              </label>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                <button type="button" className="back-button" onClick={() => setDocModalOpen(false)} disabled={savingSinExpiry}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-doc-button"
                  disabled={savingSinExpiry || !!uploadingDoc[docModalType]}
                  onClick={() => submitDocModal()}
                >
                  {savingSinExpiry || uploadingDoc[docModalType] ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isDialogOpen && (
        <div
          className="documents-modal-overlay"
          onClick={() => setIsDialogOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
        >
          <div
            className="documents-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 8,
              width: 420,
              maxWidth: "90%",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              overflow: "hidden"
            }}
          >
            <div className="documents-modal-header" style={{ padding: "12px 16px", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div className="documents-modal-title" style={{ fontWeight: 600 }}>{dialogDocType}</div>
              <button className="documents-modal-close" onClick={() => setIsDialogOpen(false)} style={{ border: 0, background: "transparent", fontSize: 18, cursor: "pointer" }}>×</button>
            </div>
            <div className="documents-modal-body" style={{ padding: 16 }}>
              <div className="documents-modal-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <span className="documents-modal-filename" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{dialogDocType}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    className="documents-modal-open"
                    onClick={() => {
                      const url = dialogDocType ? uploadedDocs[dialogDocType] : "";
                      if (url) {
                        window.open(url, "_blank");
                      } else {
                        showError("File URL not available. Try again in a moment.");
                      }
                    }}
                    style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", background: "#f7f7f7", cursor: "pointer" }}
                  >
                    Open
                  </button>
                  <button
                    className="documents-modal-remove"
                    onClick={async () => {
                      if (!dialogDocType) return;
                      const confirmRemove = await showConfirm(`Remove ${dialogDocType}?`);
                      if (!confirmRemove) return;
                      // Update canonical list by clearing only this type
                      const list = loadDocsList();
                      const nextList = list.map((d) => d.type === dialogDocType ? { ...d, url: "", file_id: null, expiry_date: "" } : d);
                      saveDocsList(nextList);
                      setUploadedDocs(toMapFromList(nextList));
                      // Track deletion locally so server-fetched files are ignored
                      try {
                        const raw = localStorage.getItem(`deletedDocs_${accountId}`);
                        const listDel = (() => { try { const d = JSON.parse(raw || '[]'); return Array.isArray(d) ? d : []; } catch { return []; } })();
                        if (!listDel.includes(dialogDocType)) listDel.push(dialogDocType);
                        localStorage.setItem(`deletedDocs_${accountId}`, JSON.stringify(listDel));
                      } catch {}
                      setIsDialogOpen(false);
                      showSuccess(`${dialogDocType} removed`);
                    }}
                    style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #f0caca", background: "#ffecec", color: "#b00020", cursor: "pointer" }}
                  >
                    Remove
                  </button>
                </div>
              </div>
              {dialogDocType === "SIN Document" ? (
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #eee" }}>
                  <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
                    {sinExpiryLabel}
                    <DateInput
                      className="documents-expiry-input"
                      value={dialogSinExpiry}
                      onChange={(v) => { setDialogSinExpiry(v); if (v) setSinExpiryError(""); }}
                    />
                    {sinExpiryError && <span style={{ color: "#b00020", fontSize: 12 }}>{sinExpiryError}</span>}
                  </label>
                  <button
                    type="button"
                    className="submit-doc-button"
                    style={{ marginTop: 12 }}
                    disabled={savingSinExpiry}
                    onClick={() => saveSinExpiryFromView()}
                  >
                    {savingSinExpiry ? "Saving…" : "Save expiry date"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
